<template>
    <div class="custom-select-container">
        <button 
            type="button"
            :id="id"
            class="custom-select-button"
            :class="{ 'has-selection': modelValue }"
            @click="toggleMenu"
            @blur="closeOnClickOutside ? handleBlur : null"
            ref="selectButton"
            :disabled="disabled"
        >
            <span class="select-text">
                <img 
                    v-if="selectedOption && selectedOption.icon" 
                    :src="selectedOption.icon" 
                    :alt="selectedOption.label" 
                    class="option-icon" 
                />
                {{ selectedOption?.label || placeholder }}
            </span>
            <i class="pi pi-angle-down select-arrow" :class="{ 'arrow-up': menuOpen }"></i>
        </button>
        
        <!-- Dropdown Menu -->
        <div v-if="menuOpen" :id="`dropdown-${id}`" ref="dropdownMenu" class="dropdown-menu" :style="dropdownStyle" @wheel="handleWheel">
                <!-- Search input (fixed) -->
                <div v-if="showSearch" class="dropdown-search">
                    <i class="pi pi-search search-icon"></i>
                    <input 
                        type="text" 
                        v-model="searchQuery"
                        placeholder="Search..."
                        class="search-input"
                        @click.stop
                        @mousedown.stop
                    />
                </div>
                
                <!-- Header (fixed) -->
                <div v-if="showHeader && headerColumns.length" class="dropdown-header">
                    <div 
                        v-for="(column, idx) in headerColumns.slice(0, -1)" 
                        :key="idx"
                        class="header-column"
                        :style="{ width: column.width || 'auto', minWidth: column.minWidth || 'auto' }"
                    >
                        {{ column.label }}
                    </div>
                    <div class="header-volume">
                        {{ headerColumns[headerColumns.length - 1]?.label }}
                    </div>
                </div>
                
                <!-- Options list -->
                <div class="dropdown-options-list">
                    <button
                        v-for="option in filteredOptions"
                        :key="option.value"
                        type="button"
                        class="dropdown-option"
                        :class="{ 'option-disabled': option.disabled }"
                        @click="selectOption(option)"
                        :disabled="option.disabled"
                    >
                        <span v-if="option.index !== undefined" class="option-index">{{ option.index }}</span>
                        
                        <!-- Trading pair with icon wrapper -->
                        <div v-if="option.price" class="option-pair-wrapper">
                            <img
                                v-if="option.icon"
                                :src="option.icon"
                                :alt="option.label"
                                class="option-icon"
                            />
                            <span class="option-label">{{ option.label }}</span>
                        </div>
                        
                        <!-- Regular option without wrapper -->
                        <template v-else>
                            <img
                                v-if="option.icon"
                                :src="option.icon"
                                :alt="option.label"
                                class="option-icon"
                            />
                            <span class="option-label">{{ option.label }}</span>
                        </template>
                        
                        <span v-if="option.price" class="option-price">{{ option.price }}</span>
                        <span v-if="option.badge" class="option-badge" v-html="option.badge"></span>
                    </button>
                </div>
            </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'

const props = defineProps({
    id: {
        type: String,
        default: ''
    },
    modelValue: {
        type: [String, Number],
        default: ''
    },
    options: {
        type: Array,
        required: true,
        // Expected format: [{ value: 'id', label: 'Display Name', icon?: 'path', disabled?: boolean, badge?: 'text', index?: number, price?: string }]
    },
    placeholder: {
        type: String,
        default: 'Select option'
    },
    disabled: {
        type: Boolean,
        default: false
    },
    showHeader: {
        type: Boolean,
        default: false
    },
    headerColumns: {
        type: Array,
        default: () => []
        // Expected format: [{ label: 'Column Name', width: '50px' }]
    },
    closeOnClickOutside: {
        type: Boolean,
        default: true
    },
    showSearch: {
        type: Boolean,
        default: false
    },
    menuPlacement: { type: String, default: 'bottom', validator: (value) => ['top', 'bottom'].includes(value) }
})

const emit = defineEmits(['update:modelValue', 'click'])

const menuOpen = ref(false)
const selectButton = ref(null)
const dropdownMenu = ref(null)
const dropdownStyle = ref({})
const searchQuery = ref('')

const selectedOption = computed(() => {
    return props.options.find(option => option.value === props.modelValue)
})

const filteredOptions = computed(() => {
    if (!searchQuery.value) {
        return props.options
    }
    
    const query = searchQuery.value.toLowerCase()
    return props.options.filter(option => {
        return option.label.toLowerCase().includes(query) ||
               option.value.toLowerCase().includes(query) ||
               (option.baseCoin && option.baseCoin.toLowerCase().includes(query))
    })
})

const toggleMenu = () => {
    if (!props.disabled) {
        menuOpen.value = !menuOpen.value
        if (menuOpen.value) {
            calculateDropdownPosition()
            emit('click')
            searchQuery.value = '' // Reset search when opening
        } else {
            searchQuery.value = '' // Reset search when closing
        }
    }
}

const selectOption = (option) => {
    if (!option.disabled) {
        emit('update:modelValue', option.value)
        menuOpen.value = false
        // Remove focus from button after selection
        if (selectButton.value) {
            selectButton.value.blur()
        }
    }
}

const handleBlur = () => {
    // Close dropdown when button loses focus
    // Small delay to allow click on option to register
    setTimeout(() => {
        menuOpen.value = false
    }, 150)
}

// Prevent page scroll when scrolling inside dropdown
const handleWheel = (event) => {
    event.preventDefault()
    event.stopPropagation()
    
    const optionsList = dropdownMenu.value?.querySelector('.dropdown-options-list')
    if (!optionsList) return
    
    // Scroll the options list instead of the page
    optionsList.scrollTop += event.deltaY
}

// Handle click outside to close dropdown
const calculateDropdownPosition = () => {
    if (!selectButton.value) return

    const verticalPosition = props.menuPlacement === 'top'
        ? { bottom: 'calc(100% + 2px)' }
        : { top: 'calc(100% + 2px)' }

    dropdownStyle.value = {
        position: 'absolute',
        ...verticalPosition,
        left: '0',
        minWidth: '100%',
        zIndex: '10'
    }
}

const handleClickOutside = (event) => {
    // Skip if closeOnClickOutside is disabled
    if (!props.closeOnClickOutside) {
        return
    }
    
    const menuElement = document.getElementById(`dropdown-${props.id}`)
    const buttonElement = selectButton.value

    if (menuOpen.value &&
        menuElement &&
        buttonElement &&
        !menuElement.contains(event.target) &&
        !buttonElement.contains(event.target)) {
        menuOpen.value = false
        buttonElement.blur()
    }
}
onMounted(() => {
    document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside)
})
</script>

<style lang="scss" scoped>
.custom-select-container {
    position: relative;
    width: 100%;
}

.custom-select-button {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid var(--theme-control, #4E4E4E);
    border-radius: 6px;
    background: var(--theme-inset, #2F2F2F);
    color: var(--theme-muted, #B4B4B4);
    font-size: 0.9rem;
    font-family: inherit;
    font-weight: 500;
    transition: none;
    box-sizing: border-box;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    text-align: left;
    min-height: 38px;
    
    &:focus {
        outline: none;
        border-color: var(--theme-accent-text, #ffffff);
        box-shadow: none;
    }
    
    &:hover:not(:focus):not(:disabled) {
        border-color: var(--theme-accent-text, #6a6a6a);
    }
    
    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
}
.select-text {
    flex: 1;
    color: var(--theme-muted, #B4B4B4);
    display: flex;
    align-items: center;
    gap: 0.5rem;
}
/* Selected option should use white text color */
.custom-select-button.has-selection .select-text {
    color: var(--theme-text, #ffffff);
}

.option-icon {
    width: 1.25rem;
    height: 1.25rem;
    border-radius: 50%;
    object-fit: cover;
    flex-shrink: 0;
}

.select-arrow {
    margin-left: 0.5rem;
    transition: transform 0.2s ease;
    color: var(--theme-text, #ffffff);
}

.arrow-up {
    transform: rotate(180deg);
}

/* Dropdown Menu */
.dropdown-menu {
    background: var(--theme-surface, #353535);
    border: 1px solid var(--theme-border, #666666);
    border-radius: 0.75rem;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    padding: 0;
    height: auto;
    overflow: visible;
    min-width: 200px;
    overscroll-behavior: contain;
    display: flex;
    flex-direction: column;
}

/* Search input (fixed) */
.dropdown-search {
    flex-shrink: 0;
    padding: 0.75rem 0.75rem 0.75rem 12px;
    background: var(--theme-inset, #2a2a2a);
    border-bottom: 1px solid var(--theme-border, #4a4a4a);
    display: flex;
    align-items: center;
    gap: 1rem;
    border-top-left-radius: 0.75rem;
    border-top-right-radius: 0.75rem;
}

.search-icon {
    color: var(--theme-muted, #999);
    font-size: 1.1rem;
}

.search-input {
    flex: 1;
    background: var(--theme-inset, #1a1a1a);
    border: 1px solid var(--theme-control, #4a4a4a);
    border-radius: 0.5rem;
    padding: 0.5rem 0.75rem;
    color: var(--theme-text, #ffffff);
    font-size: 0.9rem;
    outline: none;
    
    &::placeholder {
        color: var(--theme-muted, #666);
    }
    
    &:focus {
        border-color: var(--theme-accent-text, #666);
        background: var(--theme-inset, #252525);
    }
}

/* Dropdown Header */
.dropdown-header {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 0 0.75rem 7px;
    background: var(--theme-inset, #2a2a2a);
    border-bottom: 1px solid var(--theme-border, #4a4a4a);
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--theme-muted, #999);
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.header-column {
    display: flex;
    align-items: center;
    white-space: nowrap;
    
    &:first-child {
        justify-content: center;
    }
}

.header-volume {
    margin-left: auto;
    padding-right: 14px;
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--theme-muted, #999);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    white-space: nowrap;
    text-align: right;
}

/* Options list container with scroll */
.dropdown-options-list {
    flex-shrink: 0;
    max-height: 310px;
    overflow-y: auto;
    padding: 0.5rem 7px 0.5rem 0.5rem;
    overscroll-behavior: contain;
    
    /* Custom scrollbar */
    &::-webkit-scrollbar {
        width: 6px;
    }
    
    &::-webkit-scrollbar-track {
        background: transparent;
        border-radius: 3px;
        margin: 0.5rem 0;
    }
    
    &::-webkit-scrollbar-thumb {
        background: var(--theme-scrollbar-thumb, rgba(255, 255, 255, 0.3));
        border-radius: 3px;
        
        &:hover {
            background: var(--theme-scrollbar-hover, rgba(255, 255, 255, 0.5));
        }
    }
    
    /* Firefox scrollbar */
    scrollbar-width: thin;
    scrollbar-color: var(--theme-scrollbar-thumb, rgba(255, 255, 255, 0.3)) transparent;
}

.dropdown-option {
    width: 100%;
    padding: 0.5rem 2px 0.5rem 0;
    background: none;
    border: none;
    color: var(--theme-text, #ffffff);
    text-align: left;
    cursor: pointer;
    border-radius: 0.5rem;
    font-size: 0.9rem;
    transition: none;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    
    &:hover:not(.option-disabled) {
        background: var(--theme-interaction-hover, rgba(255, 255, 255, 0.1));
    }
    
    &:focus:not(.option-disabled) {
        outline: none;
        background: var(--theme-selected, rgba(255, 255, 255, 0.1));
    }
    
    &.option-disabled {
        cursor: not-allowed;
        opacity: 0.5;
        
        .option-label {
            color: var(--theme-muted, #B4B4B4);
        }
        
        .option-icon {
            opacity: 0.5;
        }
    }
}

.option-index {
    min-width: 28px;
    text-align: center;
    font-size: 0.85rem;
    color: var(--theme-muted, #999);
    font-weight: 500;
    flex-shrink: 0;
}

.option-pair-wrapper {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    min-width: 135px;
    flex-shrink: 0;
    
    .option-label {
        white-space: nowrap;
        padding-left: 0; // Override padding for trading pairs
    }
}

.option-label {
    flex: 1;
    padding-left: 10px; // For all other dropdowns
}

.option-price {
    font-size: 0.85rem;
    color: var(--theme-text, #ffffff);
    font-weight: 500;
}

.option-badge {
    min-width: 72px;
    margin-left: auto;
    font-size: 0.75rem;
    color: var(--theme-muted, #B4B4B4);
    background: var(--theme-hover, rgba(180, 180, 180, 0.1));
    padding: 0.125rem 0.375rem;
    border-radius: 0.25rem;
    font-weight: 400;
    letter-spacing: 0.025em;
    text-align: right;
}


</style>
